import * as UTIF from "utif2";

export interface TiffPage {
  width: number;
  height: number;
  imageData: ImageData;
}

const COMPRESSAO_JPEG_ANTIGA = 6;
const COMPRESSAO_JPEG_NOVA = 7;

export async function decodeTiff(buffer: ArrayBuffer): Promise<TiffPage[]> {
  const ifds = UTIF.decode(buffer);

  if (!ifds.length) {
    throw new Error("Arquivo TIFF vazio ou inválido.");
  }

  const paginas: TiffPage[] = [];

  for (const ifd of ifds) {
    const compressao = obterTagNumerico(ifd, 259)?.[0];
    const isJpeg = compressao === COMPRESSAO_JPEG_ANTIGA || compressao === COMPRESSAO_JPEG_NOVA;

    paginas.push(isJpeg ? await decodePaginaJpeg(buffer, ifd) : decodePaginaPadrao(buffer, ifd));
  }

  return paginas;
}

// UTIF.js decodifica corretamente TIFFs sem compressão, PackBits, LZW, Deflate e CCITT G3/G4,
// mas seu decodificador JPEG interno corrompe imagens reais (linhas embaralhadas/fantasma).
// Para compressão 6/7 reconstruímos um JPEG autônomo por strip/tile e usamos o decodificador
// nativo do navegador via createImageBitmap, que é robusto.
function decodePaginaPadrao(buffer: ArrayBuffer, ifd: UTIF.IFD): TiffPage {
  UTIF.decodeImage(buffer, ifd);
  const rgba = UTIF.toRGBA8(ifd);
  const imageData = new ImageData(new Uint8ClampedArray(rgba), ifd.width, ifd.height);
  return { width: ifd.width, height: ifd.height, imageData };
}

async function decodePaginaJpeg(buffer: ArrayBuffer, ifd: UTIF.IFD): Promise<TiffPage> {
  const largura = obterTagNumerico(ifd, 256)?.[0];
  const altura = obterTagNumerico(ifd, 257)?.[0];

  if (!largura || !altura) {
    throw new Error("Dimensões do TIFF não encontradas.");
  }

  const tabelasJpeg = obterTagBytes(ifd, 347);
  const offsets = obterTagNumerico(ifd, 273) ?? obterTagNumerico(ifd, 324) ?? [];
  const tamanhos = obterTagNumerico(ifd, 279) ?? obterTagNumerico(ifd, 325) ?? [];
  const larguraBloco = obterTagNumerico(ifd, 322)?.[0] ?? largura;
  const alturaBloco = obterTagNumerico(ifd, 278)?.[0] ?? obterTagNumerico(ifd, 323)?.[0] ?? altura;
  const colunasDeBlocos = Math.max(1, Math.ceil(largura / larguraBloco));

  if (!offsets.length || offsets.length !== tamanhos.length) {
    throw new Error("Não foi possível localizar os dados da imagem JPEG no TIFF.");
  }

  const canvas = document.createElement("canvas");
  canvas.width = largura;
  canvas.height = altura;
  const contexto = canvas.getContext("2d");

  if (!contexto) {
    throw new Error("Não foi possível criar o contexto de desenho para o TIFF.");
  }

  for (let indice = 0; indice < offsets.length; indice++) {
    const bytesBloco = new Uint8Array(buffer, offsets[indice], tamanhos[indice]);
    const jpeg = montarJpegAutonomo(tabelasJpeg, bytesBloco);
    // .slice() força um ArrayBuffer próprio: o tipo Uint8Array<ArrayBufferLike> do TS aceitaria
    // um SharedArrayBuffer, que BlobPart não aceita — o buffer original nunca é compartilhado.
    const bitmap = await createImageBitmap(new Blob([jpeg.slice()], { type: "image/jpeg" }));

    const coluna = indice % colunasDeBlocos;
    const linha = Math.floor(indice / colunasDeBlocos);
    contexto.drawImage(bitmap, coluna * larguraBloco, linha * alturaBloco);
    bitmap.close();
  }

  return { width: largura, height: altura, imageData: contexto.getImageData(0, 0, largura, altura) };
}

function montarJpegAutonomo(tabelas: Uint8Array | undefined, bloco: Uint8Array): Uint8Array {
  if (!tabelas) return bloco;

  const semEoi = tabelas.subarray(0, tabelas.length - 2);
  const semSoi = bloco.subarray(2);

  const resultado = new Uint8Array(semEoi.length + semSoi.length);
  resultado.set(semEoi, 0);
  resultado.set(semSoi, semEoi.length);
  return resultado;
}

function obterTagNumerico(ifd: UTIF.IFD, codigo: number): number[] | undefined {
  const valor = (ifd as Record<string, unknown>)[`t${codigo}`];
  return Array.isArray(valor) ? (valor as number[]) : undefined;
}

function obterTagBytes(ifd: UTIF.IFD, codigo: number): Uint8Array | undefined {
  const valor = (ifd as Record<string, unknown>)[`t${codigo}`];
  return valor instanceof Uint8Array ? valor : undefined;
}
